/**
 * Visual preview of the announcement email (matches backend Cloud Function HTML structure and styling).
 */

interface AnnouncementEmailPreviewProps {
  subjectLine: string;
  recipientName: string;
  recipientEmail: string;
  exampleNote?: string;
  eventName: string;
  eventDateLabel: string;
  location: string;
  message: string;
  messageEmptyHint?: string;
  includeRsvpLink: boolean;
  rsvpUrl: string;
  includeRsvpDashboardLink: boolean;
  rsvpDashboardUrl: string;
  includePaymentLink: boolean;
  paymentSample: { formattedBalance: string; paymentUrl: string } | null;
}

export function AnnouncementEmailPreview({
  subjectLine,
  recipientName,
  recipientEmail,
  exampleNote,
  eventName,
  eventDateLabel,
  location,
  message,
  messageEmptyHint = 'Your message will appear here.',
  includeRsvpLink,
  rsvpUrl,
  includeRsvpDashboardLink,
  rsvpDashboardUrl,
  includePaymentLink,
  paymentSample,
}: AnnouncementEmailPreviewProps) {
  const displayMessage = message.trim() ? message : messageEmptyHint;
  const year = new Date().getFullYear();

  return (
    <div className="rounded-lg border border-border bg-muted/20 p-3 sm:p-4">
      <p className="mb-3 font-mono text-xs text-muted-foreground">
        <span className="font-semibold text-foreground">Subject:</span> {subjectLine}
      </p>

      {exampleNote ? (
        <p className="mb-4 rounded-md border border-dashed border-border bg-background px-3 py-2 text-xs text-muted-foreground">
          {exampleNote}
        </p>
      ) : null}

      <div className="mx-auto max-w-[600px] overflow-hidden rounded border border-[#ddd] bg-[#f9f9f9] text-[#333] shadow-sm">
        <div className="bg-[#1E3A5F] px-5 py-5 text-center text-white">
          <h2 className="m-0 text-2xl font-normal leading-tight">Event announcement</h2>
        </div>

        <div className="border border-t-0 border-[#ddd] bg-[#f9f9f9] px-0 py-0">
          <div className="px-5 pb-6 pt-6 sm:px-8">
            <p className="mb-3 text-[15px] leading-relaxed">
              Dear {recipientName},
            </p>
            <p className="mb-4 text-[15px] leading-relaxed">
              The organisers of <strong>{eventName}</strong> have sent you an important update:
            </p>

            <div className="my-5 border-l-4 border-[#D4AF37] bg-white p-5">
              <p
                className={`m-0 whitespace-pre-wrap text-[15px] leading-relaxed ${
                  !message.trim() ? 'text-muted-foreground italic' : ''
                }`}
              >
                {displayMessage}
              </p>
            </div>

            <p className="mb-2 text-[15px] font-semibold leading-relaxed">Event details:</p>
            <ul className="mb-4 list-disc pl-6 text-[15px] leading-relaxed">
              <li>
                <strong>Date:</strong> {eventDateLabel}
              </li>
              <li>
                <strong>Venue:</strong> {location || 'TBC'}
              </li>
            </ul>

            {includeRsvpLink ? (
              <div className="my-8 text-center">
                <a
                  href={rsvpUrl}
                  className="inline-block rounded-[5px] bg-[#D4AF37] px-8 py-3 font-bold text-[#1E3A5F] no-underline"
                  onClick={(e) => e.preventDefault()}
                >
                  View event and RSVP
                </a>
                <p className="mt-2 break-all text-center text-xs text-muted-foreground">{rsvpUrl}</p>
              </div>
            ) : null}

            {includeRsvpDashboardLink ? (
              <div className="my-8 text-center">
                <a
                  href={rsvpDashboardUrl}
                  className="inline-block rounded-[5px] bg-[#D4AF37] px-8 py-3 font-bold text-[#1E3A5F] no-underline"
                  onClick={(e) => e.preventDefault()}
                >
                  RSVP Dashboard
                </a>
                <p className="mt-2 break-all text-center text-xs text-muted-foreground">{rsvpDashboardUrl}</p>
              </div>
            ) : null}

            {includePaymentLink ? (
              <div className="my-8 text-center">
                {paymentSample ? (
                  <>
                    <p className="mb-2 text-base font-bold text-[#1E3A5F]">
                      Outstanding balance: {paymentSample.formattedBalance}
                    </p>
                    <a
                      href={paymentSample.paymentUrl}
                      className="inline-block rounded-[5px] bg-[#D4AF37] px-8 py-3 font-bold text-[#1E3A5F] no-underline"
                      onClick={(e) => e.preventDefault()}
                    >
                      Make a payment
                    </a>
                    <p className="mt-2 break-all text-center text-xs text-muted-foreground">
                      {paymentSample.paymentUrl}
                    </p>
                  </>
                ) : (
                  <div className="rounded-md border border-dashed border-[#ccc] bg-white/80 px-4 py-3 text-left text-sm text-[#555]">
                    <p className="m-0 font-medium text-[#1E3A5F]">Payment section (example)</p>
                    <p className="mt-1 mb-0 text-xs leading-snug">
                      Recipients who have an outstanding balance on their invoice will see their amount and a payment
                      button with a personalised link. There is no unpaid invoice on this event to demo with live figures.
                    </p>
                  </div>
                )}
              </div>
            ) : null}

            <p className="text-[15px] leading-relaxed">
              If you have any questions, please contact the event organisers.
            </p>
          </div>

          <div className="border-t border-[#eee] px-5 py-5 text-center text-xs text-[#666] sm:px-8">
            <p className="m-0 mb-1">This is an automated message from Dining Steward.</p>
            <p className="m-0">
              &copy; {year} Dining Steward. All rights reserved.
            </p>
          </div>
        </div>
      </div>

      <p className="mt-3 text-center text-xs text-muted-foreground">
        Example addressee: {recipientName} &lt;{recipientEmail}&gt;
      </p>
    </div>
  );
}
